<template>
  <BaseLoader v-if="!$store.getters.isLoaded(['orders'])" />
  <div v-else>
    <div class="d-flex justify-content-between align-items-center" ref="container">
      <h1 class="h4" style="margin: 0.5rem 0 0.75rem 0.5rem;">
        <div v-if="resident">{{ this.residentIndex + 1 }}. {{ formatResidentName({ resident }) }}</div>
        <div v-else>{{ type | startCase }}</div>
      </h1>
      <div v-if="this.type !== 'kioskOrder'">
        <button @click="printNameTag" class="btn btn-outline-primary mr-3" ref="button">
          Name
        </button>
        <button @click="printReceipt" class="btn btn-outline-primary mr-3" ref="button">
          Receipt
        </button>
        <Printer ref="printer" />
      </div>
    </div>
    <PackList :items="items" @item-clicked="openItemLayer" />
    <PackLayer
      v-if="packLayerItemIndex !== null"
      :item="{ ...items[packLayerItemIndex], itemIndex: packLayerItemIndex }"
      @close="packLayerItemIndex = null"
      @setPackedAmount="setPackedAmount"
      @setAvailability="setAvailability"
      @setPartiallyAvailable="setPartiallyAvailable"
    />
    <div style="position: fixed; bottom: 3rem; right: 3rem; font-size: 1.5rem;">
      <button
        class="btn btn-lg btn-primary ml-2 all-packed-button"
        v-if="allItemsPacked"
        @click="finishStep"
        v-blur-on-click
      >
        ✓ Finish
      </button>
    </div>
    <Scanner :expectations="['ean']" :redirect="{ ean: false }" @scan="handleScan" />
  </div>
</template>

<script>
import PackLayer from '@/components/PackLayer'
import PackList from '@/components/PackList'
import Printer from '@/components/Printer'
import Scanner from '@/components/Scanner'
import { auth, db, fb } from '@/shared/firebase'

export default {
  components: { PackList, PackLayer, Printer, Scanner },
  data() {
    return {
      packLayerItemIndex: null
    }
  },
  created() {
    this.$route.meta.backLink = `/orders/${this.$route.params.orderId}`
  },
  computed: {
    orderId() {
      return this.$route.params.orderId
    },
    order() {
      return _.find(this.$store.state.orders, { id: this.orderId })
    },
    type() {
      return this.$route.params.type
    },
    residentIndex() {
      return parseFloat(this.$route.params.residentIndex)
    },
    resident() {
      if (this.type !== 'resident') return null
      return this.order.residents[this.residentIndex]
    },
    items() {
      let items = []

      if (typeof this.order !== 'undefined') {
        if (this.type === 'resident') items = this.resident.shoppingList.items
        if (this.type === 'homeOrder') items = this.order.homeOrder?.shoppingList?.items
        if (this.type === 'kioskOrder') items = this.order.kioskOrder?.items
      }

      items?.forEach((item, index) => {
        items[index].boxSpaces = []
        _.filter(this.order.pickedItems, { productId: item.productId }).forEach(pickedItem => {
          if (typeof pickedItem.boxSpaceId === 'string') items[index].boxSpaces.push(pickedItem.boxSpaceId)
        })
      })
      return items
    },
    allItemsPacked() {
      return checkIfAllItemsArePacked(this.items)
    }
  },
  methods: {
    handleScan(scan) {
      const productId = scan.data.id
      const increaseAmount = parseFloat(_.find(scan.data.eans, { ean: scan.q }).amount)
      this.pack({ productId, increaseAmount, product: scan.data })
    },
    openItemLayer(index) {
      this.packLayerItemIndex = index
    },
    pack(params) {
      const { productId, increaseAmount, totalAmount, product } = params
      let error = false

      let items = null
      const itemIndex = _.findIndex(this.items, { productId: productId })

      if (this.type === 'kioskOrder') items = _.cloneDeep(this.order.kioskOrder.items)
      if (this.type === 'homeOrder') items = _.cloneDeep(this.order.homeOrder.shoppingList.items)
      if (this.type === 'resident') items = _.cloneDeep(this.order.residents[this.residentIndex].shoppingList.items)

      const item = items[itemIndex]
      if (item) {
        const newAmount = increaseAmount ? (item.packed || 0) + increaseAmount : totalAmount
        if (item.amount - newAmount >= 0) {
          items[itemIndex].packed = newAmount
        } else {
          error = {
            title: 'Amount Exceeded',
            text: `${product.brandName} ${product.label} is only required ${item.amount}x`
          }
        }
      } else {
        error = {
          title: `Produkt Not Needed`,
          text: `${product.brandName} ${product.label} hasn't been ordered by this resident`
        }
      }

      if (error) {
        this.$notify({
          group: 'main',
          type: 'scan-warning',
          duration: 15000,
          ...error
        })
      } else {
        const orderRef = db.doc(`orders/${this.orderId}`)
        if (this.type === 'kioskOrder') orderRef.update({ ['kioskOrder.items']: items })
        if (this.type === 'homeOrder') orderRef.update({ ['homeOrder.shoppingList.items']: items })
        if (this.type === 'resident') {
          const residents = _.cloneDeep(this.order.residents)
          residents[this.residentIndex].shoppingList.items = items
          orderRef.update({ residents })
        }
      }
      this.logActivity('pack')
    },
    setPackedAmount(params) {
      const { index, amount } = params
      if (this.type === 'resident') {
        const residents = _.cloneDeep(this.order.residents)
        residents[this.residentIndex].shoppingList.items[index].packed = amount
        db.doc(`orders/${this.order.id}`).update({
          residents
        })
      } else if (this.type === 'homeOrder') {
        const homeOrder = _.cloneDeep(this.order.homeOrder)
        homeOrder.shoppingList.items[index].packed = amount
        db.doc(`orders/${this.order.id}`).update({
          homeOrder
        })
      } else if (this.type === 'kioskOrder') {
        const kioskOrder = _.cloneDeep(this.order.kioskOrder)
        kioskOrder.items[index].packed = amount
        db.doc(`orders/${this.order.id}`).update({
          kioskOrder
        })
      }
      this.logActivity('pack')
    },
    setAvailability(available) {
      const residents = _.cloneDeep(this.order.residents)
      const homeOrder = _.cloneDeep(this.order.homeOrder)
      const kioskOrder = _.cloneDeep(this.order.kioskOrder)
      if (this.resident) {
        residents[this.residentIndex].shoppingList.items[this.packLayerItemIndex].notAvailable = !available
        db.doc(`orders/${this.order.id}`).update({
          residents
        })
      }
      if (this.type === 'homeOrder') {
        homeOrder.shoppingList.items[this.packLayerItemIndex].notAvailable = !available
        db.doc(`orders/${this.order.id}`).update({
          homeOrder
        })
      } else if (this.kioskOrder) {
        kioskOrder.items[this.packLayerItemIndex].notAvailable = !available
        db.doc(`orders/${this.order.id}`).update({
          kioskOrder
        })
      }
      if (!available && this.resident) {
        this.printItemUnavailableHandout(
          residents[this.residentIndex].shoppingList.items[this.packLayerItemIndex].productId
        )
      } else if (!available && this.type === 'homeOrder') {
        this.printItemUnavailableHandout(homeOrder.shoppingList.items[this.packLayerItemIndex].productId)
      } else if (!available && this.kioskOrder) {
        this.printItemUnavailableHandout(kioskOrder.items[this.packLayerItemIndex].productId)
      }
    },
    setPartiallyAvailable(partiallyAvailable) {
      const residents = _.cloneDeep(this.order.residents)
      const homeOrder = _.cloneDeep(this.order.homeOrder)
      const kioskOrder = _.cloneDeep(this.order.kioskOrder)
      if (this.type === 'resident') {
        residents[this.residentIndex].shoppingList.items[
          this.packLayerItemIndex
        ].partiallyAvailable = partiallyAvailable
        db.doc(`orders/${this.order.id}`).update({
          residents
        })
      } else if (this.type === 'homeOrder') {
        homeOrder.shoppingList.items[this.packLayerItemIndex].partiallyAvailable = partiallyAvailable
        db.doc(`orders/${this.order.id}`).update({
          homeOrder
        })
      } else if (this.type === 'kioskOrder') {
        kioskOrder.items[this.packLayerItemIndex].partiallyAvailable = partiallyAvailable
        db.doc(`orders/${this.order.id}`).update({
          kioskOrder
        })
      }
      if (partiallyAvailable && this.resident) {
        this.printItemPartiallyAvailableHandout(
          residents[this.residentIndex].shoppingList.items[this.packLayerItemIndex].productId
        )
      } else if (partiallyAvailable && this.homeOrder) {
        this.printItemPartiallyAvailableHandout(homeOrder.shoppingList.items[this.packLayerItemIndex].productId)
      } else if (partiallyAvailable && this.kioskOrder) {
        this.printItemPartiallyAvailableHandout(kioskOrder.items[this.packLayerItemIndex].productId)
      }
    },
    printNameTag() {
      const { resident } = this
      const receipt = {
        titleLines: [],
        itemLines: [],
        sumLines: [],
        receiptCount: false
      }
      if (resident) {
        if (resident.residentNumber) {
          receipt.titleLines.push(
            ` ${resident.gender === 'female' ? 'Bewohnerin' : 'Bewohner'} ${resident.residentNumber} \n`
          )
        } else {
          receipt.titleLines.push(` ${resident.gender === 'female' ? 'Frau' : 'Herr'} \n`)
          receipt.titleLines.push(` ${resident.firstName ? resident.firstName + ' ' : ''}${resident.lastName} `)
        }
      }
      if (this.type === 'homeOrder') {
        receipt.titleLines.push(' Hausbedarf ')
      }
      this.$refs.printer.printReceipt(receipt)
    },
    printReceipt() {
      if (!this.allItemsPacked) {
        return alert('Es sind noch nicht alle Artikel gepackt.\nBitte packe alle Artikel bevor du den Bon druckst.')
      }
      const { resident, order } = this
      const deliveryFee = order.deliveryFee || 0.85

      const receipt = {
        titleLines: [],
        itemLines: [],
        sumLines: [],
        receiptCount: false
      }

      if (resident) {
        if (resident.residentNumber) {
          receipt.titleLines.push(
            ` ${resident.gender === 'female' ? 'Bewohnerin' : 'Bewohner'} ${resident.residentNumber} \n`
          )
        } else {
          receipt.titleLines.push(` ${resident.gender === 'female' ? 'Frau' : 'Herr'} \n`)
          receipt.titleLines.push(` ${resident.firstName ? resident.firstName + ' ' : ''}${resident.lastName} `)
        }
      }

      if (this.type === 'homeOrder') {
        receipt.titleLines.push(' Hausbedarf ')
      }

      let receiptSum = 0
      let containerDeposit = 0

      this.items.forEach(item => {
        let amount = item.amount
        if (item.partiallyAvailable) amount = item.packed
        if (!item.notAvailable) {
          const product = _.find(this.$store.state.products, { id: item.productId })
          if (product.containerDeposit) containerDeposit += amount * product.containerDeposit
          const sum = amount * product.liveRetailPrice
          receiptSum = receiptSum + sum
          receipt.itemLines.push({
            label: `${amount}x ${product.brandName} ${product.label}\n${product.packageSize}${product.packageUnit}`,
            sum: `${amount > 1 ? 'je ' : ''}${parseFloat(product.liveRetailPrice)
              .toFixed(2)
              .replace('.', ',')} €`
          })
        }
      })

      receipt.itemLines.push({
        label: 'Liefergebühr',
        sum: `${deliveryFee.toFixed(2).replace('.', ',')} €`
      })

      if (containerDeposit) {
        receipt.itemLines.push({
          label: 'Pfand',
          sum: `${containerDeposit.toFixed(2).replace('.', ',')} €`
        })
      }

      receiptSum = receiptSum + deliveryFee + containerDeposit
      receipt.sumLines.push(`Summe: ${receiptSum.toFixed(2).replace('.', ',')} €`)

      if (this.type !== 'kioskOrder') this.$refs.printer.printReceipt(receipt)
    },
    printItemUnavailableHandout(productId) {
      const product = this.$store.state.products.find(p => p.id === productId)
      if (this.resident) {
        this.$refs.printer.printItemUnavailableHandout({
          resident: this.resident,
          product
        })
      } else {
        this.$refs.printer.printItemUnavailableHandout({
          homeOrder: this.homeOrder,
          product
        })
      }
    },
    printItemPartiallyAvailableHandout(productId) {
      const product = this.$store.state.products.find(p => p.id === productId)
      if (this.resident) {
        this.$refs.printer.printItemPartiallyAvailableHandout({
          resident: this.resident,
          product
        })
      } else {
        this.$refs.printer.printItemPartiallyAvailableHandout({
          homeOrder: this.homeOrder,
          product
        })
      }
    },
    async finishStep() {
      let nextPage = {
        name: 'orderDetail',
        params: { orderId: this.orderId }
      }

      if (this.type === 'resident') {
        const residents = _.cloneDeep(this.order.residents)
        residents[this.residentIndex].shoppingList.completed = true
        let remainingResidents = _.cloneDeep(residents)
          .splice(parseFloat(this.residentIndex) + 1)
          .filter(r => !r.shoppingList.completed)
        if (remainingResidents[0]) {
          const residentIndex = residents.findIndex(r => r.id === remainingResidents[0].id)
          nextPage = {
            name: 'orderPack',
            params: { orderId: this.orderId, type: 'resident', residentIndex }
          }
        }
        db.doc(`orders/${this.orderId}`).update({ residents })
      }

      if (this.type === 'homeOrder') {
        const homeOrder = _.cloneDeep(this.order.homeOrder)
        homeOrder.shoppingList.completed = true
        db.doc(`orders/${this.orderId}`).update({ homeOrder })
      }

      if (this.type === 'kioskOrder') {
        const kioskOrder = _.cloneDeep(this.order.kioskOrder)
        kioskOrder.completed = true
        db.doc(`orders/${this.orderId}`).update({ kioskOrder })
      }
      this.logActivity('finishStep')
      this.$router.push(nextPage)
    },
    logActivity(action) {
      const today = new Date().toISOString().substr(0, 10)
      db.doc(`private/logs/warehouseActivities/${today}`).set(
        {
          [auth.currentUser.uid]: fb.firestore.FieldValue.arrayUnion({
            action,
            timestamp: new Date(),
            orderId: this.orderId
          })
        },
        { merge: true }
      )
    }
  },
  watch: {
    items(state, prevState) {
      if (typeof this.order !== 'undefined') {
        if (checkIfAllItemsArePacked(state) && !checkIfAllItemsArePacked(prevState) && this.type !== 'kioskOrder')
          this.printReceipt()
      }
    }
  },
  filters: {
    startCase(str) {
      return _.startCase(str)
    }
  }
}

const checkIfAllItemsArePacked = items => {
  let allPacked = true
  items.forEach(item => {
    let { amount, packed, notAvailable, partiallyAvailable } = item
    if (typeof packed !== 'number') packed = 0
    if (amount > packed && notAvailable !== true) {
      if (partiallyAvailable !== true) allPacked = false
    }
  })
  return allPacked
}
</script>

<style lang="scss" scoped>
.all-packed-button {
  &:focus,
  &:hover {
    background: red !important;
  }
}
</style>
